// Customer-360 lifecycle action bar: suspend / unsuspend / ban / force-logout /
// impersonate. Every sensitive action goes through the SHARED ConfirmDialog
// (_shared/ConfirmDialog) stating the consequence; all are server-side audited
// (LOCKED rules). Styling reuses the shared adm-* action-bar / secret-box classes
// (_shared/shared.css) so this matches the Devices action bar 1:1.
//
// impersonate: per task, the admin console DISPLAYS the returned token (echo) but
// does NOT auto-switch identity — auto-applying an impersonation token in the admin
// SPA is a footgun. The token is shown ONCE in an adm-secret-box with a copy button
// + a prominent "this is audited" warning; the intended flow is documented inline.
//
// Action body: mirrors DeviceActionBar — the backend accepts {reason, case_id} on
// suspend / ban / force-logout (handler/admin.go adminActionRequest; 02 §4.3 will make
// reason required), and REQUIRES case_id on impersonate (empty → 403
// IMPERSONATION_DENIED, service/admin.go). The impersonate dialog therefore hard-gates
// its confirm button on a non-empty case_id; the other actions capture both as
// optional audit context. unsuspend reads no body, so none is collected or sent.

import { useEffect, useState } from 'react';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { actionErrorMessage } from '../_shared/actionError';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import type { ConfirmTone } from '../_shared/ConfirmDialog';
import { actionGate } from './userModel';
import { sendPasswordReset, updateUserPhone, userAction } from './usersApi';
import type { AdminUser, PasswordResetChannel, UserAction } from './usersApi';

interface ActionSpec {
  key: UserAction;
  label: string;
  enabled: boolean;
  btnClass: string;
  tone: ConfirmTone;
  title: string;
  consequence: string;
  confirmLabel: string;
  /** prominent extra warning banner (impersonate) */
  warning?: string;
  /** show the reason/case_id capture fields (actions whose endpoint reads a body) */
  collectContext?: boolean;
  /** case_id must be non-empty before confirm (backend rejects impersonate without it) */
  caseIdRequired?: boolean;
  /** helper line under the capture fields */
  contextHint?: string;
}

interface Result {
  action: UserAction | 'phone' | `reset-${PasswordResetChannel}`;
  ok: boolean;
  message: string;
  /** impersonate token echo (shown once, never auto-applied). */
  token?: string;
  requestId?: string | null;
}

export function UserActionBar({ user, onChanged }: { user: AdminUser; onChanged: () => void }) {
	const { role } = useAuth();
	const gate = actionGate(user);
	const name = user.display_name || user.email || user.user_id;

  const [pending, setPending] = useState<ActionSpec | null>(null);
  const [pendingReset, setPendingReset] = useState<PasswordResetChannel | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [reason, setReason] = useState('');
  const [caseId, setCaseId] = useState('');
	const [dialogError, setDialogError] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState(user.phone_e164 ?? '');

  useEffect(() => {
    setPhoneDraft(user.phone_e164 ?? '');
  }, [user.phone_e164]);

	if (!canAdminWrite(role)) {
		return (
			<div className="card adm-actionbar">
				<div className="adm-actionbar-head">
					<h2 className="adm-card-title">帳號處置</h2>
					<span className="pill pill-neutral">唯讀</span>
				</div>
				<p className="text-secondary" style={{ margin: 0 }}>
					目前角色僅能檢視使用者資料與稽核紀錄。
				</p>
			</div>
		);
	}

	// 依風險分兩排:治理型(中性,ghost)—解除停權/強制登出;高風險(停權/封禁/模擬登入)
	// 用共用 .adm-risk-group(左側 danger 色條)圈起,與「儲存手機」等資料維護鍵區隔開,
	// 讓操作者一眼判斷這是會改變帳號狀態的危險區,而非與其他鍵同權重並排。
	const governanceSpecs: ActionSpec[] = [
    {
      key: 'unsuspend',
      label: '解除停權',
      enabled: gate.unsuspend,
      btnClass: 'btn-ghost',
      tone: 'primary',
      title: `解除 ${name} 的停權？`,
      consequence: '解除停權後此帳號將恢復正常登入與使用權限。此操作會被稽核記錄。',
      confirmLabel: '確認解除',
    },
    {
      key: 'force-logout',
      label: '強制登出',
      enabled: gate.forceLogout,
      btnClass: 'btn-ghost',
      tone: 'warn',
      title: `強制登出 ${name}？`,
      consequence: '此帳號目前在所有裝置上的登入工作階段都會立即失效，使用者下次操作時需要重新登入；帳號本身不受影響。此操作會被稽核記錄。',
      confirmLabel: '確認強制登出',
      collectContext: true,
    },
  ];

  const riskSpecs: ActionSpec[] = [
    {
      key: 'suspend',
      label: '停權',
      enabled: gate.suspend,
      btnClass: 'btn-ghost-warn',
      tone: 'warn',
      title: `停權 ${name}？`,
      consequence: '停權後此帳號將無法登入或使用服務，直到解除停權為止；既有的登入工作階段會被失效。此操作會被稽核記錄。',
      confirmLabel: '確認停權',
      collectContext: true,
      contextHint: '建議填寫停權原因與案件編號以利稽核。',
    },
    {
      key: 'ban',
      label: '封禁',
      enabled: gate.ban,
      btnClass: 'btn-danger',
      tone: 'danger',
      title: `封禁 ${name}？`,
      consequence: '封禁是嚴重且通常不可逆的處置：帳號將被永久停用、所有工作階段立即失效，使用者無法再登入。請確認你有足夠的依據再執行。此操作會被稽核記錄。',
      confirmLabel: '我了解，確認封禁',
      collectContext: true,
      contextHint: '請填寫封禁依據（如違反服務條款）與案件編號。',
    },
    {
      key: 'impersonate',
      label: '模擬登入',
      enabled: gate.impersonate,
      btnClass: 'btn-danger',
      tone: 'danger',
      title: `以 ${name} 的身分模擬登入？`,
      consequence: '系統會簽發一組代表此使用者的存取權杖（access token）。本主控台只會「顯示」該權杖供你檢視，不會自動切換身分。請僅在客訴重現或必要排查時使用，並妥善保管、用畢即棄。',
      confirmLabel: '我了解風險，簽發權杖',
      warning: '高風險操作：模擬登入會以該使用者身分存取系統，且全程稽核留痕。請審慎使用。',
      collectContext: true,
      caseIdRequired: true,
      contextHint: '後端強制要求案件編號（缺少即拒絕簽發）；原因會一併寫入稽核。',
    },
  ];

  function openConfirm(spec: ActionSpec) {
    setResult(null);
    setReason('');
    setCaseId('');
    setDialogError(null);
    setCopied(false);
    setPending(spec);
  }

  function closeConfirm() {
    if (!busy) {
      setPending(null);
      setPendingReset(null);
      setDialogError(null);
    }
  }

  async function savePhone() {
    setBusy(true);
    setResult(null);
    setDialogError(null);
    try {
      const phone = phoneDraft.trim();
      const { requestId } = await updateUserPhone(user.user_id, phone, {
        reason: phone ? '管理員更新手機' : '管理員清除手機',
      });
      setResult({ action: 'phone', ok: true, message: phone ? '手機已更新。' : '手機已清除。', requestId });
      onChanged();
    } catch (e) {
      setResult({ action: 'phone', ok: false, message: actionErrorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  function openReset(channel: PasswordResetChannel) {
    setResult(null);
    setReason('');
    setCaseId('');
    setDialogError(null);
    setPendingReset(channel);
  }

  async function runReset(channel: PasswordResetChannel) {
    setBusy(true);
    setDialogError(null);
    try {
      const { requestId } = await sendPasswordReset(user.user_id, channel, {
        reason: reason.trim() || undefined,
        caseId: caseId.trim() || undefined,
      });
      setResult({
        action: `reset-${channel}`,
        ok: true,
        message: channel === 'sms' ? '重設簡訊已送出。' : '重設信已送出。',
        requestId,
      });
      setPendingReset(null);
      onChanged();
    } catch (e) {
      setDialogError(actionErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function run(spec: ActionSpec) {
    setBusy(true);
    setDialogError(null);
    try {
      const input = spec.collectContext
        ? { reason: reason.trim() || undefined, caseId: caseId.trim() || undefined }
        : {};
      const { data, requestId } = await userAction(user.user_id, spec.key, input);
      let token: string | undefined;
      if (spec.key === 'impersonate') {
        token =
          (typeof data.access_token === 'string' && data.access_token) ||
          // 02 §3.6 response shape — tolerate a future build aligning to the spec field name
          (typeof data.impersonation_token === 'string' && data.impersonation_token) ||
          (typeof data.token === 'string' && data.token) ||
          undefined;
      }
      setResult({ action: spec.key, ok: true, message: `${spec.label}成功。`, token, requestId });
      setPending(null);
      // refresh the 360 so status/devices reflect the change. impersonate doesn't
      // mutate the target, but a reload is harmless and keeps the flow uniform.
      onChanged();
    } catch (e) {
      // Keep the dialog open + show the error inline so the admin can retry (matches
      // DeviceActionBar). actionErrorMessage surfaces the specific 403 codes
      // (IMPERSONATION_DENIED / ADMIN_REGION_MISMATCH) instead of a blanket RBAC text.
      setDialogError(actionErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function copyToken(token: string) {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="card adm-actionbar">
      <div className="adm-actionbar-head">
        <h2 className="adm-card-title">帳號處置</h2>
        {/* 合規提示改用 pill(與「唯讀」pill 同視覺重量),不再是容易被忽略的散文小字 */}
        <span className="pill pill-info">所有處置均會稽核留痕</span>
      </div>

      {/* 中性治理:解除停權/強制登出——不改變帳號的可逆性狀態,維持一般 ghost 權重 */}
      <div className="adm-actionbar-btns">
        {governanceSpecs.map((s) => (
          <button
            key={s.key}
            className={s.btnClass}
            disabled={!s.enabled || busy}
            title={s.enabled ? s.consequence : '目前帳號狀態不適用此操作'}
            onClick={() => openConfirm(s)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* 高風險:停權/封禁/模擬登入——左側 danger 色條圈起,與上方治理鍵明確分區 */}
      <div className="adm-actionbar-btns adm-risk-group">
        {riskSpecs.map((s) => (
          <button
            key={s.key}
            className={s.btnClass}
            disabled={!s.enabled || busy}
            title={s.enabled ? s.consequence : '目前帳號狀態不適用此操作'}
            onClick={() => openConfirm(s)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* 資料維護子面板(密碼重設 / 手機編輯):用較低對比背景區隔,代表「資料維護」
          而非「帳號生殺」,避免與上方處置鍵同一視覺層級混淆操作意圖 */}
      <div className="u-maintenance-panel">
        <div className="adm-actionbar-subsection">
          <div>
            <h3 className="adm-subtitle">密碼重設</h3>
            <p className="text-tertiary adm-inline-note">{user.phone_e164 ? `SMS：${user.phone_e164}` : 'SMS：未設定手機'}</p>
          </div>
          <div className="adm-actionbar-btns">
            <button className="btn-ghost" disabled={busy || user.status !== 'active'} onClick={() => openReset('email')}>
              Email 重設
            </button>
            <button className="btn-ghost" disabled={busy || user.status !== 'active' || !user.phone_e164 || !user.phone_verified} onClick={() => openReset('sms')}>
              SMS 重設
            </button>
          </div>
        </div>

        <div className="adm-phone-row">
          <label className="adm-field adm-phone-field">
            <span className="text-secondary adm-field-label">手機（E.164）</span>
            <input
              value={phoneDraft}
              onChange={(e) => setPhoneDraft(e.target.value)}
              placeholder="+886935068727"
              disabled={busy}
              inputMode="tel"
            />
          </label>
          <button className="btn-primary btn-sm" disabled={busy || phoneDraft.trim() === (user.phone_e164 ?? '')} onClick={() => void savePhone()}>
            儲存手機
          </button>
        </div>
      </div>

      {result && (
        <div className={`adm-action-result ${result.ok ? 'is-ok' : 'is-err'}`} role="status">
          <p style={{ margin: 0 }}>{result.message}</p>
          {result.requestId && (
            <p className="adm-mono adm-result-meta" style={{ margin: 0, opacity: 0.85 }}>
              request_id: {result.requestId}
            </p>
          )}
        </div>
      )}

      {/* impersonate token echo — shown ONCE, never auto-applied (footgun guard) */}
      {result?.action === 'impersonate' && result.ok && (
        <div className="adm-secret-box">
          <p className="adm-secret-warn">模擬登入權杖 — 僅顯示一次</p>
          <p style={{ margin: 0, fontSize: 'var(--font-size-md)' }} className="text-secondary">
            本主控台不會自動切換身分。如需重現使用者情境，請手動將此權杖帶入排查工具，用畢即棄。此次簽發已稽核留痕。
          </p>
          {result.token ? (
            <div className="adm-secret-row">
              <code className="adm-secret-value">{result.token}</code>
              <button className="btn-ghost" onClick={() => void copyToken(result.token as string)}>
                {copied ? '已複製' : '複製'}
              </button>
            </div>
          ) : (
            <p style={{ margin: 0 }} className="text-secondary">
              （後端未在回應中回傳權杖內容；操作已完成並留痕。）
            </p>
          )}
        </div>
      )}

      {pending && (
        <ConfirmDialog
          title={pending.title}
          confirmLabel={pending.confirmLabel}
          tone={pending.tone}
          warning={pending.warning}
          busy={busy}
          // impersonate is rejected server-side without a case_id (403 IMPERSONATION_DENIED)
          // — gate the confirm button instead of letting the call fail.
          confirmDisabled={pending.caseIdRequired ? caseId.trim() === '' : false}
          error={dialogError}
          onConfirm={() => void run(pending)}
          onCancel={closeConfirm}
          body={
            !pending.collectContext ? (
              pending.consequence
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-6)' }}>
                <p style={{ margin: 0 }}>{pending.consequence}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
                  <label className="adm-field">
                    <span className="text-secondary adm-field-label">原因（選填）</span>
                    <input
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      disabled={busy}
                      placeholder="例：客訴重現"
                      style={{ width: '100%' }}
                    />
                  </label>
                  <label className="adm-field">
                    <span className="text-secondary adm-field-label">
                      {pending.caseIdRequired ? '案件編號（必填）' : '案件編號（選填）'}
                    </span>
                    <input
                      value={caseId}
                      onChange={(e) => setCaseId(e.target.value)}
                      disabled={busy}
                      placeholder="例：CS-1234"
                      style={{ width: '100%' }}
                    />
                  </label>
                  {pending.contextHint && <p className="adm-hint" style={{ marginTop: 0 }}>{pending.contextHint}</p>}
                </div>
              </div>
            )
          }
        />
      )}
      {pendingReset && (
        <ConfirmDialog
          title={pendingReset === 'sms' ? `寄送簡訊重設 給 ${name}？` : `寄送電子郵件重設 給 ${name}？`}
          confirmLabel={pendingReset === 'sms' ? '確認寄送簡訊' : '確認寄送 Email'}
          tone="warn"
          busy={busy}
          error={dialogError}
          onConfirm={() => void runReset(pendingReset)}
          onCancel={closeConfirm}
          body={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-6)' }}>
              <p style={{ margin: 0 }}>
                系統會簽發 15 分鐘有效的密碼重設連結，並透過{pendingReset === 'sms' ? '簡訊' : 'Email'}送給此使用者。此操作會被稽核記錄。
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
                <label className="adm-field">
                  <span className="text-secondary adm-field-label">原因（選填）</span>
                  <input value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} placeholder="例：忘記密碼" style={{ width: '100%' }} />
                </label>
                <label className="adm-field">
                  <span className="text-secondary adm-field-label">案件編號（選填）</span>
                  <input value={caseId} onChange={(e) => setCaseId(e.target.value)} disabled={busy} placeholder="例：CS-1234" style={{ width: '100%' }} />
                </label>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
}
